import { useLocation, useNavigate } from '@reach/router';
import { SubscriptionsApi } from 'api/subscriptionsApi';
import { AppPath } from 'appConstants';
import { CardPaymentElements } from 'cardPayment/CardPaymentElements';
import { CardOrderRequest, CardPaymentErrorAction, CardPaymentReceipt } from 'cardPayment/types';
import { useCardPayment } from 'cardPayment/useCardPayment';
import { CardPaymentProcessing } from 'components/cardPayment/CardPaymentProcessing';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import FormStripeCardField from 'components/formComponents/FormStripeCardField';
import FormTextField from 'components/formComponents/FormTextField';
import { PricingSummaryDetails } from 'components/pricingSummaryDetails/PricingSummaryDetails';
import { IPriceInfo } from 'pages/newSubscription/models';
import { PackagePromo, PackageResult } from 'subscriptionOptions/types';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { SetStateAction, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  selectAddOrUpgradeUserDetails,
  selectFinanceEmail,
  selectPoNumber,
  selectRegistrationToken,
  selectSecondExistingUser,
  selectSecondUserDetails,
  selectQuotesId,
  selectQuotePromoCodeSlots,
  selectMembershipOptInfo,
  selectSchoolAccountContext,
} from 'reducers/misc';
import { selectSignedIn } from 'reducers/auth';
import * as Yup from 'yup';

type PaymentDetailsFormProps = { onSubmit: (receipt: CardPaymentReceipt) => void };

type PaymentDetailsFormValues = { cardholderName: string };

const InnerPaymentDetailsForm: React.FC<PaymentDetailsFormProps> = ({ onSubmit }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const isNewSchool = pathname === AppPath.SUBSCRIBE;

  const { pay, stage, isPaying } = useCardPayment(isNewSchool ? 'public' : 'school');

  const [error, setError] = useState<string>('');
  const [errorAction, setErrorAction] = useState<CardPaymentErrorAction | undefined>();
  const registrationToken = useSelector(selectRegistrationToken);
  const poNumber = useSelector(selectPoNumber);
  const FinanceEmail = useSelector(selectFinanceEmail);
  const secondUserDetails = useSelector(selectSecondUserDetails);
  const secondExistingUserDetails = useSelector(selectSecondExistingUser);
  const addOrUpgradeUserDetails = useSelector(selectAddOrUpgradeUserDetails);
  const existingMembershipOptInfo = useSelector(selectMembershipOptInfo);
  const quotesId: number | null = useSelector(selectQuotesId);
  const promoCodeSlots = useSelector(selectQuotePromoCodeSlots);
  const isSignedIn = useSelector(selectSignedIn);
  const storedAccountContext = useSelector(selectSchoolAccountContext);
  const schoolAccountContext = isSignedIn && pathname === AppPath.NEW_SUBSCRIPTION ? storedAccountContext : null;

  /** The pairs the summary priced, so payment sends what produced the amount shown. */
  const [packagePromos, setPackagePromos] = useState<PackagePromo[]>([]);
  /** The total the summary shows, which the order must match. */
  const [pricedTotal, setPricedTotal] = useState<number | null>(null);
  const [pricedPackages, setPricedPackages] = useState<PackageResult[]>([]);
  /** Bumped to remount the summary, which prices it again. */
  const [summaryVersion, setSummaryVersion] = useState<number>(0);

  const quotePackages =
    existingMembershipOptInfo && existingMembershipOptInfo[0]
      ? existingMembershipOptInfo[0].quotePackages
      : null;

  const initialValues: PaymentDetailsFormValues = { cardholderName: '' };

  const bNothingToPay = pricedTotal === 0;

  const validationSchema = Yup.object({
    cardholderName: bNothingToPay
      ? Yup.string()
      : Yup.string().required("Please enter the cardholder's name"),
  });

  const onPriced = (priceInfo: SetStateAction<IPriceInfo>) => {
    if (typeof priceInfo !== 'function') {
      setPricedTotal(priceInfo.total);
    }
  };

  const buildRequest = (expectedGrossAmount: number): CardOrderRequest => {
    if (isNewSchool) {
      return {
        orderType: 'NEW_SCHOOL',
        registrationToken,
        quotesId,
        packagePromos,
        expectedGrossAmount,
        poNumber,
        financeEmail: FinanceEmail,
      };
    }

    return {
      orderType: 'ADD',
      packagePromos,
      expectedGrossAmount,
      poNumber,
      financeEmail: FinanceEmail,
      bAddSecondSchoolUser: !!(addOrUpgradeUserDetails && addOrUpgradeUserDetails.addSecondSchoolUser),
      bChooseAddOrUpgrade: !!(addOrUpgradeUserDetails && addOrUpgradeUserDetails.chooseAddOrUpgrade),
      ...(secondUserDetails
        ? {
            firstName: secondUserDetails.firstName,
            lastName: secondUserDetails.lastName,
            emailAddress: secondUserDetails.emailAddress,
          }
        : secondExistingUserDetails
        ? { mainAccountHolderSchoolUsersId: secondExistingUserDetails.mainAccountHolderSchoolUsersId }
        : {}),
    };
  };

  const buildReceipt = (outcome: 'paid' | 'received', total: number): CardPaymentReceipt => ({
    outcome,
    items: pricedPackages.map((pkg) => ({
      title: pkg.subjectsName,
      detail: `${pkg.generalPackagesName} - ${pkg.subscriptionTypesName}`,
    })),
    total,
  });

  const handleFormSubmission = (
    values: PaymentDetailsFormValues,
    { setSubmitting }: FormikHelpers<PaymentDetailsFormValues>
  ) => {
    // Never pay before the summary has priced: the promos it sends are the ones it priced.
    if (packagePromos.length === 0 || pricedTotal === null) {
      setSubmitting(false);
      setError('Sorry, something went wrong. Please try again.');
      setErrorAction(undefined);
      return;
    }

    setError('');
    setErrorAction(undefined);

    const total = pricedTotal;

    pay(buildRequest(total), values.cardholderName, (outcome) => {
      if (outcome.kind === 'failed') {
        setSubmitting(false);
        setError(outcome.message);
        setErrorAction(outcome.action);

        if (outcome.action === 'reprice') {
          setPricedTotal(null);
          setSummaryVersion((version) => version + 1);
        }
        return;
      }

      if (!isNewSchool) {
        checkFreeTrialAvailable();
      }

      onSubmit(buildReceipt(outcome.kind, total));
    });
  };

  const checkFreeTrialAvailable = async () => {
    const result = await SubscriptionsApi.getSubscriptionsList();

    if (result.data) {
      const { bCanAddFreeTrial } = result.data;

      if (
        !bCanAddFreeTrial &&
        document.getElementsByClassName('HeaderDropdownMenu')[0] &&
        document
          .getElementsByClassName('HeaderDropdownMenu')[0]
          .getElementsByClassName('LinkItem UpgradeLink')[0]
      ) {
        document
          .getElementsByClassName('HeaderDropdownMenu')[0]
          .getElementsByClassName('LinkItem UpgradeLink')[0]
          .remove();
      }
    }
  };

  const errorActionButton = () => {
    if (errorAction === 'signIn' || (errorAction === 'subscriptions' && isNewSchool)) {
      return (
        <button type="button" className="Btn BtnOrange" onClick={() => navigate(AppPath.SIGN_IN)}>
          Sign in
        </button>
      );
    }

    if (errorAction === 'subscriptions') {
      return (
        <button type="button" className="Btn BtnOrange" onClick={() => navigate(AppPath.MANAGESCHOOL)}>
          View subscriptions
        </button>
      );
    }

    return null;
  };

  return (
    <div className="Form">
      <FormGeneralError message={error} />
      {errorAction && <div className="CardPaymentErrorAction">{errorActionButton()}</div>}
      <CardPaymentProcessing stage={stage} />
      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmission}
        validationSchema={validationSchema}
      >
        {({ isSubmitting }) => (
          <Form>
            <div className="PricingColRow">
              <div className="PCRowInner">
                <div className="LeftCol">
                  {bNothingToPay ? (
                    <p>There is nothing to pay, so no card details are needed.</p>
                  ) : (
                    <>
                      <FormTextField
                        field={'cardholderName'}
                        label={"Cardholder's name"}
                        required={true}
                        disabled={isPaying}
                      />
                      <FormStripeCardField
                        field={'card'}
                        label={'Card '}
                        required={true}
                        disabled={isPaying}
                      />
                    </>
                  )}
                </div>
                <div className="RightCol LowerBlock">
                  <PricingSummaryDetails
                    key={summaryVersion}
                    addPromoStep={false}
                    isDisabled={isSubmitting || isPaying}
                    isSubmitting={isSubmitting || isPaying}
                    btnText="Subscribe"
                    existingMembershipOptInfo={existingMembershipOptInfo}
                    accountContext={schoolAccountContext}
                    onPricedPromos={setPackagePromos}
                    setPriceInfo={onPriced}
                    onPackagesPriced={setPricedPackages}
                    {...(quotePackages && quotePackages.length > 0
                      ? { promoCodeSlots, quotePackages }
                      : {})}
                  />
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const PaymentDetailsForm: React.FC<PaymentDetailsFormProps> = (props) => (
  <CardPaymentElements>
    <InnerPaymentDetailsForm {...props} />
  </CardPaymentElements>
);

export default PaymentDetailsForm;
